import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ServiceInfoSection from './components/ServiceInfoSection';
import LocationSection from '../add-listing/components/LocationSection';
import PricingSection from '../add-listing/components/PricingSection';
import ScheduleSection from '../add-listing/components/ScheduleSection';
import MediaSection from '../add-business/components/MediaSection';
import ServicePreview from './components/ServicePreview';
import subscriptionService from '../../services/subscriptionService';

const AddService = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState({});
  const [servicesCount, setServicesCount] = useState(1);

  // Premium gating state - now dynamic
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    services: [
      {
        id: 1,
        name: '',
        businessName: '',
        category: '',
        shortDescription: '',
        fullDescription: '',
        tags: '',
        location: {
          isOnline: false,
          address: '',
          city: '',
          postcode: '',
          fullAddress: ''
        },
        pricing: {
          type: '',
          fixedPrice: '',
          minPrice: '',
          maxPrice: '',
          suggestedDonation: '',
          currencyNote: '',
          earlyBird: false,
          concessions: false
        },
        schedule: {
          eventType: '',
          startDate: '',
          endDate: '',
          startTime: '',
          endTime: '',
          duration: '',
          frequency: '',
          courseWeeks: '',
          courseStartDate: '',
          capacity: '',
          bookingRequired: false,
          dropInsWelcome: false
        },
        media: {
          images: [],
          featuredImageId: null,
          website: '',
          bookingLink: ''
        }
      }
    ],
    currentServiceIndex: 0
  });

  const steps = [
    { id: 1, title: 'Service Info', icon: 'FileText' },
    { id: 2, title: 'Location', icon: 'MapPin' },
    { id: 3, title: 'Pricing', icon: 'PoundSterling' },
    { id: 4, title: 'Schedule', icon: 'Calendar' },
    { id: 5, title: 'Media', icon: 'Image' }
  ];

  // Enhanced authentication and premium status check
  useEffect(() => {
    const checkAccessPermissions = async () => {
      setIsLoading(true);
      
      try {
        const token = localStorage.getItem('authToken');
        const fromWhatWeOffer = location?.state?.fromWhatWeOffer === true;
        const businessId = location?.state?.businessId;

        // Check if user is authenticated
        if (!token) {
          navigate('/login', { 
            state: { 
              returnTo: '/add-service',
              message: 'Please log in to add services to your business'
            }
          });
          return;
        }

        // Check if they came from an authorized source (What We Offer section)
        if (!fromWhatWeOffer || !businessId) {
          navigate('/business-detail', {
            state: {
              accessDenied: true,
              message: 'Service forms can only be accessed from the "What We Offer" section of your business profile.',
              businessId: businessId
            }
          });
          return;
        }

        // Get dynamic subscription status
        const subscription = await subscriptionService?.getBusinessSubscription(businessId);
        setSubscriptionData(subscription);

        // Check if user has premium subscription
        if (!subscription?.isPremium) {
          navigate('/business-detail', {
            state: {
              upgradeRequired: true,
              message: 'Premium subscription required to add services. Upgrade to the £9.99/year plan to access service management.',
              businessId: businessId
            }
          });
          return;
        }

        // Check if user can add more services
        if (!subscription?.canAddServices) {
          navigate('/business-detail', {
            state: {
              limitReached: true,
              message: `You've reached your service limit (${subscription?.servicesUsed}/${subscription?.servicesLimit}). Upgrade to Premium Plus for £4.99 to add 4 more services.`,
              businessId: businessId
            }
          });
          return;
        }

        // All checks passed
        setIsAuthorized(true);
        
        // Update localStorage for consistency
        localStorage.setItem('isPremium', 'true');
        
        // Set max services count based on available slots
        const availableSlots = subscription?.servicesLimit - subscription?.servicesUsed;
        setServicesCount(Math.min(1, availableSlots));
        
      } catch (error) {
        console.error('Error checking access permissions:', error);
        navigate('/business-detail', {
          state: {
            error: true,
            message: 'Failed to verify access permissions. Please try again.',
            businessId: location?.state?.businessId
          }
        });
      } finally {
        setIsLoading(false);
      }
    };

    checkAccessPermissions();
  }, [navigate, location]);

  const validateStep = (step) => {
    const newErrors = {};
    const currentService = formData?.services?.[formData?.currentServiceIndex];

    switch (step) {
      case 1:
        if (!currentService?.name?.trim()) newErrors.name = 'Service name is required';
        if (!currentService?.businessName?.trim()) newErrors.businessName = 'Business name is required';
        if (!currentService?.category) newErrors.category = 'Category is required';
        if (!currentService?.shortDescription?.trim()) newErrors.shortDescription = 'Short description is required';
        if (!currentService?.fullDescription?.trim()) newErrors.fullDescription = 'Full description is required';
        break;

      case 2:
        if (!currentService?.location?.isOnline) {
          if (!currentService?.location?.address?.trim()) newErrors['location.address'] = 'Address is required';
          if (!currentService?.location?.city?.trim()) newErrors['location.city'] = 'City is required';
          if (!currentService?.location?.postcode?.trim()) {
            newErrors['location.postcode'] = 'Postcode is required';
          } else if (!/^[A-Z]{1,2}[0-9R][0-9A-Z]? [0-9][A-Z]{2}$/?.test(currentService?.location?.postcode)) {
            newErrors['location.postcode'] = 'Please enter a valid UK postcode';
          }
        }
        break;

      case 3:
        if (!currentService?.pricing?.type) newErrors['pricing.type'] = 'Pricing type is required';
        if (currentService?.pricing?.type === 'fixed' && !currentService?.pricing?.fixedPrice) {
          newErrors['pricing.fixedPrice'] = 'Fixed price is required';
        }
        if (currentService?.pricing?.type === 'range') {
          if (!currentService?.pricing?.minPrice) newErrors['pricing.minPrice'] = 'Minimum price is required';
          if (!currentService?.pricing?.maxPrice) newErrors['pricing.maxPrice'] = 'Maximum price is required';
        }
        break;

      case 4:
        if (!currentService?.schedule?.eventType) newErrors['schedule.eventType'] = 'Event type is required';
        if (currentService?.schedule?.eventType === 'one-time') {
          if (!currentService?.schedule?.startDate) newErrors['schedule.startDate'] = 'Event date is required';
          if (!currentService?.schedule?.startTime) newErrors['schedule.startTime'] = 'Start time is required';
        }
        if (currentService?.schedule?.eventType === 'recurring') {
          if (!currentService?.schedule?.frequency) newErrors['schedule.frequency'] = 'Frequency is required';
          if (!currentService?.schedule?.startDate) newErrors['schedule.startDate'] = 'Start date is required';
          if (!currentService?.schedule?.startTime) newErrors['schedule.startTime'] = 'Start time is required';
        }
        if (currentService?.schedule?.eventType === 'course') {
          if (!currentService?.schedule?.courseStartDate) newErrors['schedule.courseStartDate'] = 'Course start date is required';
          if (!currentService?.schedule?.courseWeeks) newErrors['schedule.courseWeeks'] = 'Number of weeks is required';
          if (!currentService?.schedule?.startTime) newErrors['schedule.startTime'] = 'Session time is required';
        }
        break;

      case 5:
        // Media validation is optional
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps?.length) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const addNewService = () => {
    const availableSlots = (subscriptionData?.servicesLimit || 0) - (subscriptionData?.servicesUsed || 0);
    const maxNewServices = Math.min(5, availableSlots);
    
    if (servicesCount < maxNewServices) {
      const newServiceId = Math.max(...formData?.services?.map(s => s?.id)) + 1;
      const newService = {
        id: newServiceId,
        name: '',
        businessName: formData?.services?.[0]?.businessName || '',
        category: '',
        shortDescription: '',
        fullDescription: '',
        tags: '',
        location: {
          isOnline: false,
          address: '',
          city: '',
          postcode: '',
          fullAddress: ''
        },
        pricing: {
          type: '',
          fixedPrice: '',
          minPrice: '',
          maxPrice: '',
          suggestedDonation: '',
          currencyNote: '',
          earlyBird: false,
          concessions: false
        },
        schedule: {
          eventType: '',
          startDate: '',
          endDate: '',
          startTime: '',
          endTime: '',
          duration: '',
          frequency: '',
          courseWeeks: '',
          courseStartDate: '',
          capacity: '',
          bookingRequired: false,
          dropInsWelcome: false
        },
        media: {
          images: [],
          featuredImageId: null,
          website: '',
          bookingLink: ''
        }
      };

      setFormData(prev => ({
        ...prev,
        services: [...prev?.services, newService],
        currentServiceIndex: prev?.services?.length
      }));
      setServicesCount(prev => prev + 1);
      setCurrentStep(1); // Reset to first step for new service
    }
  };

  const switchService = (index) => {
    setFormData(prev => ({
      ...prev,
      currentServiceIndex: index
    }));
    setCurrentStep(1); // Reset to first step when switching services
  };

  const removeService = (index) => {
    if (formData?.services?.length > 1) {
      const newServices = formData?.services?.filter((_, i) => i !== index);
      const newCurrentIndex = index === formData?.currentServiceIndex 
        ? Math.max(0, index - 1) 
        : formData?.currentServiceIndex > index 
          ? formData?.currentServiceIndex - 1 
          : formData?.currentServiceIndex;

      setFormData(prev => ({
        ...prev,
        services: newServices,
        currentServiceIndex: newCurrentIndex
      }));
      setServicesCount(prev => prev - 1);
    }
  };

  const handleSubmit = async (isDraft = false) => {
    if (!isDraft && !validateAllServices()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // TODO: Implement actual service creation via Supabase
      // This would create services in the services table with business_id
      // and update the subscription services_used count
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Success - redirect to profile or confirmation
      navigate('/user-profile', { 
        state: { 
          message: isDraft 
            ? 'Services saved as draft successfully!' 
            : `${formData?.services?.length} service(s) submitted successfully!` 
        }
      });
    } catch (error) {
      console.error('Error submitting services:', error);
      setErrors({ submit: 'Failed to submit services. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateAllServices = () => {
    let isValid = true;
    formData?.services?.forEach((_, serviceIndex) => {
      const originalIndex = formData?.currentServiceIndex;
      setFormData(prev => ({ ...prev, currentServiceIndex: serviceIndex }));
      
      for (let i = 1; i <= steps?.length; i++) {
        if (!validateStep(i)) {
          isValid = false;
        }
      }
      
      setFormData(prev => ({ ...prev, currentServiceIndex: originalIndex }));
    });
    return isValid;
  };

  const renderStepContent = () => {
    const currentService = formData?.services?.[formData?.currentServiceIndex];
    
    switch (currentStep) {
      case 1:
        return (
          <ServiceInfoSection 
            formData={formData} 
            setFormData={setFormData} 
            errors={errors} 
            currentServiceIndex={formData?.currentServiceIndex}
          />
        );
      case 2:
        return (
          <LocationSection 
            formData={currentService} 
            setFormData={(updates) => {
              setFormData(prev => ({
                ...prev,
                services: prev?.services?.map((service, index) => 
                  index === prev?.currentServiceIndex 
                    ? { ...service, ...updates }
                    : service
                )
              }));
            }} 
            errors={errors} 
          />
        );
      case 3:
        return (
          <PricingSection 
            formData={currentService} 
            setFormData={(updates) => {
              setFormData(prev => ({
                ...prev,
                services: prev?.services?.map((service, index) => 
                  index === prev?.currentServiceIndex 
                    ? { ...service, ...updates }
                    : service
                )
              }));
            }} 
            errors={errors} 
          />
        );
      case 4:
        return (
          <ScheduleSection 
            formData={currentService} 
            setFormData={(updates) => {
              setFormData(prev => ({
                ...prev,
                services: prev?.services?.map((service, index) => 
                  index === prev?.currentServiceIndex 
                    ? { ...service, ...updates }
                    : service
                )
              }));
            }} 
            errors={errors} 
          />
        );
      case 5:
        return (
          <MediaSection 
            formData={currentService} 
            setFormData={(updates) => {
              setFormData(prev => ({
                ...prev,
                services: prev?.services?.map((service, index) => 
                  index === prev?.currentServiceIndex 
                    ? { ...service, ...updates }
                    : service
                )
              }));
            }} 
            errors={errors} 
          />
        );
      default:
        return null;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Verifying subscription and access permissions...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If not authorized, this component won't render due to navigation
  // But keeping this as fallback
  if (!isAuthorized || !subscriptionData?.isPremium) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center">
              <Icon name="Lock" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
                Access Restricted
              </h1>
              <p className="text-muted-foreground font-body mb-6">
                Premium subscription required to access service management features.
              </p>
              <Button
                onClick={() => navigate('/business-detail')}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Return to Business Profile
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const availableSlots = (subscriptionData?.servicesLimit || 0) - (subscriptionData?.servicesUsed || 0);
  const maxNewServices = Math.min(5, availableSlots);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Premium Badge with dynamic info */}
          <div className="mb-4">
            <div className="flex items-center space-x-2">
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-2">
                <Icon name="Crown" size={14} />
                <span>Premium Service Management</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {subscriptionData?.tier === 'premium_plus' 
                  ? `Premium Plus - ${subscriptionData?.servicesUsed}/${subscriptionData?.servicesLimit} services used`
                  : `Premium - ${subscriptionData?.servicesUsed}/${subscriptionData?.servicesLimit} services used`
                }
              </p>
            </div>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <button
                onClick={() => navigate('/business-detail', { 
                  state: { businessId: location?.state?.businessId }
                })}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-gentle"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">Add Services</h1>
                <p className="text-muted-foreground mt-1">
                  Add up to {maxNewServices} services ({availableSlots} slots available)
                </p>
              </div>
            </div>

            {/* Service Tabs */}
            <div className="flex items-center space-x-2 mb-6 overflow-x-auto">
              {formData?.services?.map((service, index) => (
                <div key={service?.id} className="flex items-center space-x-1">
                  <button
                    onClick={() => switchService(index)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData?.currentServiceIndex === index
                        ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="Star" size={14} />
                    <span>Service {index + 1}</span>
                    {service?.name && <span>: {service?.name}</span>}
                  </button>
                  {formData?.services?.length > 1 && (
                    <button
                      onClick={() => removeService(index)}
                      className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  )}
                </div>
              ))}
              
              {servicesCount < maxNewServices && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addNewService}
                  iconName="Plus"
                  iconPosition="left"
                  className="whitespace-nowrap"
                >
                  Add Service ({availableSlots - servicesCount} left)
                </Button>
              )}
            </div>

            {/* Progress Steps */}
            <div className="flex items-center space-x-4 overflow-x-auto pb-2">
              {steps?.map((step, index) => (
                <div key={step?.id} className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 min-w-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        currentStep >= step?.id
                          ? 'bg-primary text-white' :'bg-muted text-muted-foreground'
                      }`}
                    >
                      <Icon name={step?.icon} size={16} />
                    </div>
                    <span
                      className={`text-sm font-medium whitespace-nowrap ${
                        currentStep >= step?.id ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {step?.title}
                    </span>
                  </div>
                  {index < steps?.length - 1 && (
                    <div
                      className={`w-8 h-0.5 transition-colors ${
                        currentStep > step?.id ? 'bg-primary' : 'bg-border'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {renderStepContent()}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between pt-6 border-t border-border">
                <div>
                  {currentStep > 1 && (
                    <Button
                      variant="outline"
                      onClick={handlePrevious}
                      iconName="ChevronLeft"
                      iconPosition="left"
                    >
                      Previous
                    </Button>
                  )}
                </div>

                <div className="flex items-center space-x-3">
                  {currentStep === steps?.length && (
                    <Button
                      variant="outline"
                      onClick={() => handleSubmit(true)}
                      loading={isSubmitting}
                      iconName="Save"
                      iconPosition="left"
                    >
                      Save Draft
                    </Button>
                  )}
                  
                  {currentStep < steps?.length ? (
                    <Button
                      variant="default"
                      onClick={handleNext}
                      iconName="ChevronRight"
                      iconPosition="right"
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      onClick={() => handleSubmit(false)}
                      loading={isSubmitting}
                      iconName="Send"
                      iconPosition="left"
                    >
                      Submit Services
                    </Button>
                  )}
                </div>
              </div>

              {errors?.submit && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-destructive" />
                    <p className="text-sm text-destructive">{errors?.submit}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Preview Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-heading font-semibold text-foreground">Preview</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                    iconName={showPreview ? "EyeOff" : "Eye"}
                    iconPosition="left"
                    className="lg:hidden"
                  >
                    {showPreview ? 'Hide' : 'Show'}
                  </Button>
                </div>
                
                <div className={`${showPreview ? 'block' : 'hidden lg:block'}`}>
                  <ServicePreview 
                    formData={formData?.services?.[formData?.currentServiceIndex]} 
                    serviceNumber={formData?.currentServiceIndex + 1}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddService;